import { inspectionCatalogue } from '../data/inspectionCatalogue';
import type { InspectionCheck, InspectionResponseRecord } from '../types/inspection';
import type { SavedProperty } from '../types/property';

const MAX_ALPHA_CHECKS = 60;

export function getInspectionPlan(property: SavedProperty): InspectionCheck[] {
  const bedrooms = property.bedrooms ?? 3;
  const bathrooms = property.bathrooms ?? 2;

  const applicable = inspectionCatalogue.filter((item) => {
    if (item.propertyTypes && !item.propertyTypes.includes(property.property_type)) {
      return false;
    }

    if (item.minBedrooms && bedrooms < item.minBedrooms) {
      return false;
    }

    if (item.minBathrooms && bathrooms < item.minBathrooms) {
      return false;
    }

    return true;
  });

  // Alpha experience is intentionally bounded to a buyer-manageable plan.
  // The master catalogue can grow without exposing an unbounded checklist.
  return applicable.slice(0, MAX_ALPHA_CHECKS);
}

export function groupChecksByCategory(checks: InspectionCheck[]): Array<{
  category: string;
  checks: InspectionCheck[];
}> {
  const grouped = new Map<string, InspectionCheck[]>();

  for (const item of checks) {
    const categoryChecks = grouped.get(item.category) ?? [];
    categoryChecks.push(item);
    grouped.set(item.category, categoryChecks);
  }

  return Array.from(grouped, ([category, categoryChecks]) => ({
    category,
    checks: categoryChecks,
  }));
}

export function getInspectionProgress(
  checks: InspectionCheck[],
  responses: Record<string, InspectionResponseRecord>,
): { answered: number; total: number; percent: number } {
  const answered = checks.reduce(
    (count, item) => count + (responses[item.id] ? 1 : 0),
    0,
  );
  const total = checks.length;

  return {
    answered,
    total,
    percent: total === 0 ? 0 : Math.round((answered / total) * 100),
  };
}

export function severityToResponseSeverity(
  severity: InspectionCheck['severity'],
): InspectionResponseRecord['severity'] {
  switch (severity) {
    case 'critical':
      return 'urgent';
    case 'major':
      return 'important';
    default:
      return 'minor';
  }
}
